/**
 * Карта за формой входа.
 *
 * Не украшение: панель приложения — матовое стекло, а стеклу нужно, что
 * размывать. Без карты оно превращается в мутный серый прямоугольник.
 *
 * Карта здесь неинтерактивная и медленно плывёт: человек на этом экране
 * вводит пароль, а не изучает местность.
 */

import { useEffect, useRef } from 'react'
import maplibregl, { type Map as MapLibreMap } from 'maplibre-gl'

const STYLE_URL = 'https://tiles.versatiles.org/assets/styles/colorful/style.json'

// Полный оборот за это время. Достаточно медленно, чтобы не отвлекать.
const ROTATE_MS = 240_000

// Масштаб фона. Для справки: 4 — вся Европа, 9 — область, 12 — город,
// 14 — кварталы. Двенадцать выбраны глазами: районы читаются, мельтешения нет.
const BACKGROUND_ZOOM = 12

type Props = {
  center: { lat: number; lon: number }
}

export function BackgroundMap({ center }: Props) {
  const container = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!container.current) return

    let map: MapLibreMap | null = null
    let stopped = false

    try {
      map = new maplibregl.Map({
        container: container.current,
        style: STYLE_URL,
        center: [center.lon, center.lat],
        zoom: BACKGROUND_ZOOM,
        bearing: 0,
        interactive: false,
        attributionControl: false, // подпись показывается после входа, на рабочей карте
      })
    } catch {
      return // без карты форма всё равно работает, просто фон будет ровным
    }

    // Поворот отдан самой карте, а не нашему циклу по кадрам.
    // Выставлять угол вручную каждый кадр — значит заставлять карту заново
    // раскладывать все подписи по столько же раз; отсюда и рывки.
    // easeTo движок ведёт на своём цикле отрисовки и делает это плавно.
    const QUARTER = 90
    const step = () => {
      if (stopped || !map) return
      map.easeTo({
        bearing: map.getBearing() + QUARTER,
        duration: (ROTATE_MS / 360) * QUARTER,
        easing: (t) => t, // равномерно: разгон и торможение здесь читались бы как рывок
      })
    }

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (!reduced) {
      map.on('load', step)
      map.on('moveend', step)
    }

    // Браузер может менять размер окна без события resize — например при
    // изменении масштаба страницы. Следим за самим контейнером.
    const observer = new ResizeObserver(() => map?.resize())
    observer.observe(container.current)

    return () => {
      stopped = true
      observer.disconnect()
      map?.remove()
    }
  }, [center.lat, center.lon])

  return (
    <div className="bg-map" aria-hidden="true">
      <div ref={container} className="bg-map-canvas" />
      <div className="bg-map-veil" />
    </div>
  )
}
